// src/pages/inicio.jsx - Con soporte para internacionalización
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPlayers, getFlagUrl } from '../services/athleteDataService';
import { useLanguage } from '../LanguageContext'; // Importamos el hook de idioma

const Inicio = () => {
  // Hook de idioma
  const { t } = useLanguage();
  
  // Hook de navegación
  const navigate = useNavigate();
  
  // Estados
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [allPlayers, setAllPlayers] = useState([]);
  const [displayedPlayers, setDisplayedPlayers] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const [animationEnabled, setAnimationEnabled] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  
  // Referencias
  const heroSliderRef = useRef(null);
  const videoRef = useRef(null);
  const playersWrapperRef = useRef(null);
  
  // Constantes
  const PLAYERS_PER_PAGE = 3;
  
  // Detectar si es dispositivo móvil
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);
  
  // Función auxiliar para eliminar jugadores duplicados de la lista original
  const removeDuplicatePlayers = (players) => {
    const uniquePlayersMap = new Map();
    
    players.forEach(player => {
      if (!uniquePlayersMap.has(player.id)) {
        uniquePlayersMap.set(player.id, player);
      }
    });
    
    return Array.from(uniquePlayersMap.values());
  };
  
  // Obtener jugadores al iniciar
  useEffect(() => {
    try {
      let players = getAllPlayers();
      // Asegurarnos de que no hay duplicados en la lista original
      players = removeDuplicatePlayers(players);
      setAllPlayers(players);
      updateDisplayedPlayers(players, 0);
    } catch (error) {
      console.error('Error cargando jugadores:', error);
      // También asegurarnos que no hay duplicados en los datos de respaldo
      const uniqueFeaturedAthletes = removeDuplicatePlayers(featuredAthletes);
      setAllPlayers(uniqueFeaturedAthletes);
      updateDisplayedPlayers(uniqueFeaturedAthletes, 0);
    }
  }, []);
  
  // Datos de los jugadores destacados con imágenes en acción
  const featuredAthletes = [
    {
      id: 2,
      name: 'JUAN MANUEL DELGADO',
      position: 'Delantero',
      team: 'V-Varen Nagasaki',
      stats: { goals: 15, assists: 4, matches: 30 },
      image: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/juanma_delgado_01.png',
      actionImage: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/juanma_delgado_01.png',
      country: 'ES',
      countryCode: 'ES',
      highlights: ['Goleador de la J2 League', 'Experiencia Internacional', 'Especialista en definición']
    },
    {
      id: 8,
      name: 'MALCOLM MOYO',
      position: 'Defensa',
      team: 'Portimonense SC',
      stats: { goals: 2, assists: 3, matches: 25 },
      image: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/MOYO_01.png',
      actionImage: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/malcolm_moyo.png',
      country: 'JP',
      countryCode: 'JP',
      highlights: ['Defensa sólido', 'Buen juego aéreo', 'Experiencia internacional']
    },
    {
      id: 1,
      name: 'ANTHONY AOKI',
      position: 'Mediocampista',
      team: 'Alianza Universidad',
      stats: { goals: 3, assists: 6, matches: 22 },
      image: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/Tadashi_aoki_01.png',
      actionImage: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/anthony_aoki.png',
      country: 'PE',
      countryCode: 'PE',
      highlights: ['Control de juego', 'Pases precisos', 'Visión táctica']
    },
    {
      id: 3,
      name: 'THIAGO ALVES',
      position: 'Delantero',
      team: 'Uthai Thani FC',
      stats: { goals: 18, assists: 5, matches: 32 },
      image: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/thiago_alves_01.png',
      actionImage: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/thiago_alves_01.png',
      country: 'BR',
      countryCode: 'BR',
      highlights: ['Potencia física', 'Técnica brasileña', 'Remate de cabeza']
    },
    {
      id: 4,
      name: 'PEDRO YNAMINE',
      position: 'Portero',
      team: 'Alianza Universidad',
      stats: { goals: 0, assists: 0, matches: 28 },
      image: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/pedro_ynamine_01.png',
      actionImage: 'https://pub-8b7376b23b5a4b558c0089505b9b4b8e.r2.dev/Tadashi_aoki_01.png',
      country: 'PE',
      countryCode: 'PE',
      highlights: ['Mejor arquero L2 2024', 'Recuperación de balón', 'Atajadas decisivas']
    }
  ];
  
  // Servicios profesionales para reclutadores
  const professionalServices = [
    {
      title: t('home.services.representation.title'),
      description: t('home.services.representation.desc'),
      icon: 'trophy'
    },
    {
      title: t('home.services.scouting.title'),
      description: t('home.services.scouting.desc'),
      icon: 'chart'
    },
    {
      title: t('home.services.contracts.title'),
      description: t('home.services.contracts.desc'),
      icon: 'handshake'
    },
    {
      title: t('home.services.legal.title'),
      description: t('home.services.legal.desc'),
      icon: 'shield'
    }
  ];
  
  // Función para actualizar los jugadores mostrados en el carrusel
  const updateDisplayedPlayers = (players, page) => {
    if (!players || players.length === 0) return;
    
    // Primero, asegurarnos de que no hay IDs duplicados en la lista de jugadores
    const uniquePlayers = removeDuplicatePlayers(players);
    
    const totalPages = Math.ceil(uniquePlayers.length / PLAYERS_PER_PAGE);
    const normalizedPage = ((page % totalPages) + totalPages) % totalPages;
    
    let displayPlayers = [];
    
    for (let i = -1; i <= 1; i++) {
      const pageToShow = ((normalizedPage + i) % totalPages + totalPages) % totalPages;
      const startIdx = pageToShow * PLAYERS_PER_PAGE;
      let groupPlayers = uniquePlayers.slice(startIdx, startIdx + PLAYERS_PER_PAGE);
      
      if (groupPlayers.length < PLAYERS_PER_PAGE) {
        const remaining = PLAYERS_PER_PAGE - groupPlayers.length;
        // Evitamos duplicados también aquí al llenar el grupo
        const fillerPlayers = uniquePlayers.slice(0, remaining).filter(
          p => !groupPlayers.some(gp => gp.id === p.id)
        );
        groupPlayers = [...groupPlayers, ...fillerPlayers];
      }
      
      displayPlayers.push(...groupPlayers);
    }
    
    // Verificación adicional para asegurar que no hay duplicados en la vista final
    const finalDisplayPlayers = [];
    const seenIds = new Set();
    
    displayPlayers.forEach(player => {
      if (!seenIds.has(player.id)) {
        seenIds.add(player.id);
        finalDisplayPlayers.push(player);
      }
    });
    
    setDisplayedPlayers(finalDisplayPlayers);
    setCurrentPage(normalizedPage);
  };

  // Iniciar animaciones después de cargar la página
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationEnabled(true);
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);

  // Control del hero slider automático
  useEffect(() => {
    if (!animationEnabled) return;
    
    const interval = setInterval(() => {
      setActiveHeroSlide((prev) => (prev + 1) % featuredAthletes.length);
    }, 7000); // Más tiempo para mostrar cada atleta
    
    return () => clearInterval(interval);
  }, [featuredAthletes.length, animationEnabled]);
  
  // Detectar secciones visibles para animaciones al scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'services', 'players', 'stats'];
      const newVisible = {};
      
      sections.forEach(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const isInView = rect.top <= window.innerHeight * 0.75 && rect.bottom >= 0;
          newVisible[section] = isInView;
        }
      });
      
      setIsVisible(newVisible);
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Navegación de jugadores
  const prevPlayerGroup = () => {
    updateDisplayedPlayers(allPlayers.length > 0 ? allPlayers : featuredAthletes, currentPage - 1);
  };
  
  const nextPlayerGroup = () => {
    updateDisplayedPlayers(allPlayers.length > 0 ? allPlayers : featuredAthletes, currentPage + 1);
  };
  
  // Navegar a perfil
  const goToPlayerProfile = (playerId) => {
    navigate(`/atletas/${playerId}`);
  };
  
  // Calcular número total de páginas para indicadores
  const totalPages = Math.ceil((allPlayers.length > 0 ? allPlayers.length : featuredAthletes.length) / PLAYERS_PER_PAGE);
  
  // Renderizar un icono según su nombre (versión simplificada)
  const renderIcon = (iconName) => {
    switch (iconName) {
      case 'trophy':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        );
      case 'chart':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      case 'handshake':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
          </svg>
        );
      case 'shield':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      default:
        return null;
    }
  };
  
  // Ir a una página específica de jugadores
  const goToPlayerPage = (pageIndex) => {
    updateDisplayedPlayers(allPlayers.length > 0 ? allPlayers : featuredAthletes, pageIndex);
  };
  return (
    <div className="bg-black text-white overflow-hidden">
      {/* Hero Section - Profesional con efecto de video */}
      <section className="relative min-h-screen overflow-hidden">
        {/* Video de fondo o capa de gradiente dinámica */}
        <div className="absolute inset-0 bg-black z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-black to-transparent z-10"></div>
          
          {/* Aquí iría idealmente un video de fondo con jugadas destacadas */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900 to-black opacity-80"></div>
          
          {/* Efecto dinámico con líneas */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute left-0 top-1/4 w-full h-px bg-red-600/30 animate-growWidth"></div>
            <div className="absolute left-0 top-2/4 w-full h-px bg-red-600/20 animate-growWidthDelayed"></div>
            <div className="absolute left-0 top-3/4 w-full h-px bg-red-600/10 animate-growWidthDelayed2"></div>
          </div>
        </div>
        
        <div className="container mx-auto px-4 md:px-8 relative z-10 min-h-screen flex flex-col justify-center">
          {/* En móvil, primero muestra el jugador destacado */}
          <div className="md:hidden pb-6">
            <div className="relative h-[350px] w-full mb-6">
              {featuredAthletes.map((athlete, index) => (
                <div 
                  key={athlete.id}
                  className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center justify-center ${
                    activeHeroSlide === index ? 'opacity-100 z-10 transform scale-100' : 'opacity-0 z-0 transform scale-95'
                  }`}
                >
                  <div className="relative group cursor-pointer" onClick={() => goToPlayerProfile(athlete.id)}>
                    {/* Imagen del jugador */}
                    <div className="relative">
                      <div className="absolute -top-3 -left-3 w-16 h-16 border-2 border-red-600/40 rounded-xl z-0"></div>
                      
                      <img 
                        src={athlete.image} 
                        alt={athlete.name} 
                        className="relative z-10 w-56 object-cover rounded-xl shadow-2xl shadow-black/50"
                      />
                    </div>
                    
                    {/* Info del jugador - Versión móvil simplificada */}
                    <div className="mt-4 text-center relative">
                      {/* La etiqueta de posición se muestra arriba del nombre */}
                      <div className="mb-2">
                        <div className="inline-block px-4 py-1 bg-red-600 rounded-full text-sm font-semibold whitespace-nowrap">
                          {athlete.position}
                        </div>
                      </div>
                      
                      {/* Nombre del jugador */}
                      <h3 className="text-xl font-bold mb-2">
                        {athlete.name}
                      </h3>
                      
                      {/* Equipo y país */}
                      <div className="flex items-center justify-center gap-2 mb-3">
                        <div className="flex items-center space-x-1">
                          <img 
                            src={getFlagUrl(athlete.countryCode)} 
                            alt={`Bandera de ${athlete.country}`}
                            className="w-4 h-auto rounded-sm"
                          />
                          <span className="text-xs text-gray-400">{athlete.country}</span>
                        </div>
                        <span className="text-gray-500 text-xs">•</span>
                        <span className="text-xs text-red-500 font-medium">{athlete.team}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Indicadores de slide para móvil */}
            <div className="flex justify-center">
              <div className="flex space-x-3">
                {featuredAthletes.map((_, index) => (
                  <button 
                    key={index}
                    onClick={() => setActiveHeroSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      activeHeroSlide === index 
                        ? 'bg-red-600 scale-125' 
                        : 'bg-white/30 hover:bg-white/50'
                    }`}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-1/2 text-center md:text-left mb-12 md:mb-0">
              <div className="inline-block mb-3 px-3 py-1 bg-red-600/20 backdrop-blur-sm rounded-full text-red-500 text-sm font-semibold uppercase tracking-wider">
                {t('home.hero.exclusiveRepresentation')}
              </div>
              
              <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
                {t('home.hero.title')}
              </h1>
              
              <p className="text-lg text-gray-300 mb-8 max-w-lg">
                {t('home.hero.subtitle')}
              </p>
              
              <div className="flex flex-wrap gap-4 justify-center md:justify-start">
                <a 
                  href="/atletas" 
                  className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold transition-all duration-300 shadow-lg shadow-red-600/20"
                >
                  {t('home.hero.cta.viewAthletes')}
                </a>
                <a 
                  href="/contacto" 
                  className="px-8 py-3 border border-white hover:border-red-600 hover:text-red-600 text-white rounded-full font-bold transition-all duration-300"
                >
                  {t('home.hero.cta.contact')}
                </a>
              </div>
            </div>
            
            {/* En escritorio, el jugador aparece a la derecha */}
            <div className="md:w-1/2 relative hidden md:block">
              {/* Carrusel de jugadores destacados */}
              <div className="relative h-[450px] md:h-[500px] w-full">
                {featuredAthletes.map((athlete, index) => (
                  <div 
                    key={athlete.id}
                    className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center justify-center ${
                      activeHeroSlide === index ? 'opacity-100 z-10 transform scale-100' : 'opacity-0 z-0 transform scale-95'
                    }`}
                  >
                    <div className="relative group cursor-pointer" onClick={() => goToPlayerProfile(athlete.id)}>
                      {/* Imagen del jugador */}
                      <div className="relative">
                        <div className="absolute -top-6 -left-6 w-24 h-24 border-2 border-red-600/40 rounded-xl z-0 transition-all duration-500 group-hover:border-red-600/80"></div>
                        
                        <img 
                          src={athlete.image} 
                          alt={athlete.name} 
                          className="relative z-10 w-64 md:w-80 object-cover rounded-xl shadow-2xl shadow-black/50 transition-transform duration-500 group-hover:scale-105"
                        />
                        
                        {/* Estadísticas destacadas */}
                        <div className="absolute -bottom-6 -right-6 transform scale-0 group-hover:scale-100 transition-all duration-500 bg-gradient-to-br from-black/90 to-red-900/80 backdrop-blur-sm rounded-xl p-4 z-20 shadow-xl">
                          <div className="grid grid-cols-3 gap-3 text-center">
                            <div>
                              <div className="text-2xl font-bold text-red-500">{athlete.stats.matches}</div>
                              <div className="text-xs text-gray-300">{t('home.hero.stats.matches')}</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-red-500">{athlete.stats.goals}</div>
                              <div className="text-xs text-gray-300">{t('home.hero.stats.goals')}</div>
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-red-500">{athlete.stats.assists}</div>
                              <div className="text-xs text-gray-300">{t('home.hero.stats.assists')}</div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Info del jugador */}
                      <div className="mt-8 text-center relative">
                        <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                          <div className="px-4 py-1 bg-red-600 rounded-full text-sm font-semibold">
                            {athlete.position}
                          </div>
                        </div>
                        
                        <h3 className="text-2xl md:text-3xl font-bold mb-2">
                          {athlete.name}
                        </h3>
                        
                        <div className="flex items-center justify-center gap-3 mb-4">
                          <div className="flex items-center space-x-2">
                            <img 
                              src={getFlagUrl(athlete.countryCode)} 
                              alt={`Bandera de ${athlete.country}`}
                              className="w-5 h-auto rounded-sm"
                            />
                            <span className="text-sm text-gray-400">{athlete.country}</span>
                          </div>
                          <span className="text-gray-500">•</span>
                          <span className="text-sm text-red-500 font-medium">{athlete.team}</span>
                        </div>
                        
                        {/* Habilidades destacadas - Solo muestran en escritorio */}
                        <div className="flex flex-wrap justify-center gap-2">
                          {athlete.highlights.map((highlight, idx) => (
                            <span 
                              key={idx} 
                              className="inline-block px-3 py-1 bg-gray-800 text-gray-300 text-xs rounded-full"
                            >
                              {highlight}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Indicadores de slide */}
              <div className="absolute -bottom-10 left-0 right-0 flex justify-center">
                <div className="flex space-x-3">
                  {featuredAthletes.map((_, index) => (
                    <button 
                      key={index}
                      onClick={() => setActiveHeroSlide(index)}
                      className={`w-3 h-3 rounded-full transition-all duration-300 ${
                        activeHeroSlide === index 
                          ? 'bg-red-600 scale-125' 
                          : 'bg-white/30 hover:bg-white/50'
                      }`}
                      aria-label={`Slide ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Elemento decorativo y llamado a la acción al final de la pantalla */}
        <div className="absolute bottom-8 left-0 right-0 flex justify-center z-20 animate-bounce">
          <a href="#about" className="text-gray-400 hover:text-white transition-colors">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
        </div>
      </section>
      
      {/* Sección de estadísticas - Nueva sección para impresionar a reclutadores */}
<section 
  id="stats" 
  className={`py-20 relative bg-gradient-to-b from-black to-gray-900 overflow-hidden transition-all duration-1000 ${isVisible.stats ? 'opacity-100' : 'opacity-0'}`}
>
  <div className="absolute inset-0 z-0">
    <div className="absolute left-0 w-1/4 h-full bg-gradient-to-r from-red-900/20 to-transparent"></div>
    <div className="absolute right-0 w-1/4 h-full bg-gradient-to-l from-red-900/10 to-transparent"></div>
  </div>
  
  <div className="container mx-auto px-6 relative z-10">
    <div className={`text-center mb-16 transition-all duration-1000 delay-300 ${isVisible.stats ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
      <div className="inline-block px-3 py-1 bg-red-600/20 backdrop-blur-sm rounded-full text-red-500 text-sm font-semibold uppercase tracking-wider mb-3">
        {t('home.stats.subtitle')}
      </div>
      <h2 className="text-3xl md:text-4xl font-bold">{t('home.stats.title')}</h2>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
      {/* Métricas destacadas - Estos serían datos agregados de tus jugadores */}
      <div className={`bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 transition-all duration-1000 delay-100 ${isVisible.stats ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="h-16 w-16 bg-red-600/20 flex items-center justify-center rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        </div>
        
        <div className="text-4xl font-bold text-white mb-2">15+</div>
        <div className="text-lg mb-2">{t('home.stats.players')}</div>
        <div className="text-sm text-gray-400">{t('home.stats.players.desc')}</div>
      </div>
      
      <div className={`bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 transition-all duration-1000 delay-200 ${isVisible.stats ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="h-16 w-16 bg-red-600/20 flex items-center justify-center rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        </div>
        
        <div className="text-4xl font-bold text-white mb-2">90%</div>
        <div className="text-lg mb-2">{t('home.stats.success')}</div>
        <div className="text-sm text-gray-400">{t('home.stats.success.desc')}</div>
      </div>
      
      <div className={`bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 transition-all duration-1000 delay-300 ${isVisible.stats ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="h-16 w-16 bg-red-600/20 flex items-center justify-center rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        
        <div className="text-4xl font-bold text-white mb-2">8+</div>
        <div className="text-lg mb-2">{t('home.stats.countries')}</div>
        <div className="text-sm text-gray-400">{t('home.stats.countries.desc')}</div>
      </div>
      
      <div className={`bg-gradient-to-br from-gray-900 to-black rounded-xl p-6 transition-all duration-1000 delay-400 ${isVisible.stats ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
        <div className="h-16 w-16 bg-red-600/20 flex items-center justify-center rounded-full mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        </div>
        
        <div className="text-4xl font-bold text-white mb-2">24h</div>
        <div className="text-lg mb-2">{t('home.stats.response')}</div>
        <div className="text-sm text-gray-400">{t('home.stats.response.desc')}</div>
      </div>
    </div>
    
    <div className="mt-16 text-center">
      <a href="/contacto" className="inline-block px-8 py-3 bg-red-600 text-white font-bold rounded-full hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-600/20">
        {t('home.stats.cta')}
      </a>
    </div>
  </div>
</section>

{/* Acerca de nosotros - Ahora con enfoque profesional */}
<section 
  id="about" 
  className={`py-24 relative overflow-hidden transition-all duration-1000 ${isVisible.about ? 'opacity-100' : 'opacity-0'}`}
>
  <div className="absolute inset-0 z-0">
    <div className="absolute top-0 right-0 w-2/5 h-full opacity-10 z-0 bg-gradient-to-b from-red-900 to-transparent"></div>
    <div className="absolute bottom-0 left-0 w-2/5 h-full opacity-10 z-0 bg-gradient-to-t from-red-900 to-transparent"></div>
  </div>
  
  <div className="container mx-auto px-6 relative z-10">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <div className={`transition-all duration-1000 delay-200 ${isVisible.about ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
        <div className="inline-block px-3 py-1 bg-red-600/20 backdrop-blur-sm rounded-full text-red-500 text-sm font-semibold uppercase tracking-wider mb-3">
          {t('home.about.subtitle')}
        </div>
        
        <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('home.about.title')}</h2>
        
        <p className="text-lg text-gray-300 mb-6 leading-relaxed">
          {t('home.about.desc1')}
        </p>
        
        <p className="text-lg text-gray-300 mb-8 leading-relaxed">
          {t('home.about.desc2')}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 h-10 w-10 bg-red-600/20 flex items-center justify-center rounded-full mr-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold mb-1">{t('home.about.feature1.title')}</h3>
              <p className="text-sm text-gray-400">{t('home.about.feature1.desc')}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 h-10 w-10 bg-red-600/20 flex items-center justify-center rounded-full mr-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold mb-1">{t('home.about.feature2.title')}</h3>
              <p className="text-sm text-gray-400">{t('home.about.feature2.desc')}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 h-10 w-10 bg-red-600/20 flex items-center justify-center rounded-full mr-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold mb-1">{t('home.about.feature3.title')}</h3>
              <p className="text-sm text-gray-400">{t('home.about.feature3.desc')}</p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 h-10 w-10 bg-red-600/20 flex items-center justify-center rounded-full mr-3">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-bold mb-1">{t('home.about.feature4.title')}</h3>
              <p className="text-sm text-gray-400">{t('home.about.feature4.desc')}</p>
            </div>
          </div>
        </div>
        
        <div className="mt-10">
          <a href="/compania" className="inline-block px-6 py-2.5 border border-red-600 text-red-600 font-semibold rounded-full hover:bg-red-600 hover:text-white transition-colors duration-300">
            {t('home.about.cta')}
          </a>
        </div>
      </div>
      
      <div className={`relative transition-all duration-1000 delay-400 ${isVisible.about ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
        <div className="relative">
          <div className="absolute -top-6 -left-6 w-64 h-64 border-2 border-red-600/30 rounded-xl z-0"></div>
          <div className="absolute -bottom-6 -right-6 w-64 h-64 border-2 border-red-600/30 rounded-xl z-0"></div>
          
          <div className="relative z-10 overflow-hidden rounded-xl shadow-2xl shadow-black/50">
            {/* Aquí iría una imagen profesional del equipo APY GROUP o de jugadores en una sesión de entrenamiento */}
            <div className="aspect-video bg-gradient-to-br from-gray-900 to-black p-12 flex items-center justify-center">
              <div className="text-center">
                <div className="text-red-600 text-6xl font-bold mb-4">AFY</div>
                <div className="text-2xl font-medium text-white">GROUP</div>
                <div className="mt-4 text-sm text-gray-400">FOOTBALL MANAGEMENT</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

{/* Servicios Profesionales */}
<section 
  id="services" 
  className={`py-24 bg-gradient-to-b from-black to-gray-900 relative transition-all duration-1000 ${isVisible.services ? 'opacity-100' : 'opacity-0'}`}
>
  <div className="container mx-auto px-6 relative z-10">
    <div className={`text-center mb-16 transition-transform duration-1000 delay-200 ${isVisible.services ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
      <div className="inline-block px-3 py-1 bg-red-600/20 backdrop-blur-sm rounded-full text-red-500 text-sm font-semibold uppercase tracking-wider mb-3">
        {t('home.services.subtitle')}
      </div>
      
      <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('home.services.title')}</h2>
      <p className="max-w-2xl mx-auto text-gray-300">
        {t('home.services.desc')}
      </p>
    </div>
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
      {professionalServices.map((service, index) => (
        <div 
          key={index}
          className={`bg-gradient-to-br from-gray-900 to-black rounded-xl p-8 transition-all duration-1000 shadow-lg hover:shadow-red-900/20 ${isVisible.services ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}
          style={{ transitionDelay: `${index * 100 + 300}ms` }}
        >
          <div className="flex mb-6">
            <div className="flex-shrink-0 h-14 w-14 bg-red-600/20 rounded-lg flex items-center justify-center mr-4">
              {renderIcon(service.icon)}
            </div>
            
            <div>
              <h3 className="text-xl font-bold mb-2">{service.title}</h3>
              <p className="text-gray-400">{service.description}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
    
    <div className="mt-16 text-center">
      <a 
        href="/servicio" 
        className="inline-block px-8 py-3 border border-red-600 text-red-600 font-semibold uppercase tracking-wider rounded-full hover:bg-red-600 hover:text-white transition-all duration-300"
      >
        {t('home.services.cta')}
      </a>
    </div>
  </div>
</section>

{/* Jugadores Destacados - Carrusel de 3 a la vez */}
<section 
  id="players" 
  className={`py-24 relative transition-all duration-1000 ${isVisible.players ? 'opacity-100' : 'opacity-0'}`}
>
  <div className="absolute inset-0 z-0">
    <div className="absolute inset-0 bg-black/70 z-10"></div>
    <div className="w-full h-full bg-gradient-to-b from-gray-900 to-black"></div>
  </div>
  
  <div className="container mx-auto px-6 relative z-10">
    <div className={`text-center mb-16 transition-transform duration-1000 delay-200 ${isVisible.players ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
      <div className="inline-block px-3 py-1 bg-red-600/20 backdrop-blur-sm rounded-full text-red-500 text-sm font-semibold uppercase tracking-wider mb-3">
        {t('home.featured.subtitle')}
      </div>
      
      <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('home.featured.title')}</h2>
      <p className="max-w-2xl mx-auto text-gray-300">
        {t('home.featured.desc')}
      </p>
    </div>
    
    {/* Carrusel de jugadores mejorado */}
    <div className={`relative transition-all duration-1000 delay-400 ${isVisible.players ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
      <div className="relative overflow-hidden mx-auto max-w-7xl">
        <div 
          ref={playersWrapperRef}
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${100 / 3}%)` }}
        >
          {displayedPlayers.map((player, index) => {
            const isActive = index >= 3 && index < 6;
            
            return (
              <div 
                key={`${player.id}-${index}`}
                className={`w-full md:w-1/3 flex-shrink-0 px-4 transition-all duration-500 ${isActive ? 'scale-100 opacity-100' : 'scale-95 opacity-60'}`}
              >
                <div 
                  onClick={() => goToPlayerProfile(player.id)}
                  className="bg-gradient-to-b from-gray-900 to-black rounded-xl overflow-hidden group cursor-pointer transition-all duration-300 hover:shadow-lg hover:shadow-red-600/20 hover:-translate-y-2 h-full"
                >
                  <div className="relative">
                    <div className="relative aspect-square overflow-hidden bg-black">
                      <img 
                        src={player.image} 
                        alt={player.name} 
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
                      
                      {/* Badge del número */}
                      <div className="absolute bottom-[6.5rem] right-6 z-20 bg-black/50 backdrop-blur-sm rounded-full w-12 h-12 flex items-center justify-center text-2xl font-bold border-2 border-red-600">
                        {player.number || ''}
                      </div>
                      
                      {/* Logo APY */}
                      <div className="absolute bottom-0 left-0 right-0 flex justify-center mb-4">
                        <div className="h-8 w-24 flex items-center justify-center">
                          <div className="text-red-600 font-bold text-sm">AFY GROUP</div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-2">{player.name}</h3>
                    <p className="text-red-600 mb-3">{player.team}</p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <img 
                          src={getFlagUrl(player.countryCode)} 
                          alt={`Bandera de ${player.country}`} 
                          className="w-5 h-auto object-cover rounded-sm mr-2"
                        />
                        <span className="text-sm text-gray-400">{player.country}</span>
                      </div>
                      
                      <span className="text-sm text-white bg-red-600/20 px-3 py-1 rounded-full group-hover:bg-red-600 transition-colors duration-300">
                        {t('home.featured.viewProfile')}
                      </span>
                    </div>
                    
                    {/* Barra de progreso para alguna estadística */}
                    <div className="mt-4">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-xs text-gray-400">{player.position || t('home.featured.player')}</span>
                        <span className="text-xs text-gray-400">{t('home.featured.professional')}</span>
                      </div>
                      <div className="w-full bg-gray-800 rounded-full h-1.5">
                        <div className="bg-red-600 h-1.5 rounded-full" style={{ width: '85%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Botones de navegación */}
      <button 
        onClick={prevPlayerGroup} 
        className="absolute left-0 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center hover:bg-white hover:text-red-600 transition-all duration-300"
        aria-label={t('home.featured.prevPlayer')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
        </svg>
      </button>
      
      <button 
        onClick={nextPlayerGroup} 
        className="absolute right-0 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-red-600 rounded-full flex items-center justify-center hover:bg-white hover:text-red-600 transition-all duration-300"
        aria-label={t('home.featured.nextPlayer')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
        </svg>
      </button>
      
      {/* Indicadores de página */}
      <div className="flex justify-center mt-10 gap-2">
        {[...Array(totalPages)].map((_, index) => (
          <button 
            key={index}
            onClick={() => goToPlayerPage(index)}
            className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${currentPage === index ? 'bg-red-600 w-8' : 'bg-white/30 hover:bg-white/50'}`}
            aria-label={`${t('home.featured.page')} ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Botón Ver Todos */}
      <div className="flex justify-center mt-12">
        <a 
          href="/atletas" 
          className="inline-block px-8 py-3 bg-red-600 text-white font-semibold uppercase tracking-wider rounded-full hover:bg-red-700 transition-all duration-300 shadow-lg shadow-red-600/20"
        >
          {t('home.featured.viewAll')}
        </a>
      </div>
    </div>
  </div>
</section>

{/* Call to Action final */}
<section className="py-20 bg-gradient-to-b from-gray-900 to-black relative overflow-hidden">
  <div className="absolute inset-0 z-0">
    <div className="absolute inset-0 bg-red-900/10"></div>
  </div>
  
  <div className="container mx-auto px-6 relative z-10">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="text-3xl md:text-4xl font-bold mb-6">{t('home.cta.title')}</h2>
      <p className="text-lg text-gray-300 mb-10">
        {t('home.cta.desc')}
      </p>
      
      <div className="flex flex-wrap gap-4 justify-center">
        <a 
          href="/contacto" 
          className="px-8 py-3 bg-red-600 hover:bg-red-700 text-white rounded-full font-bold transition-all duration-300 shadow-lg shadow-red-600/20"
        >
          {t('home.cta.contact')}
        </a>
        <a 
          href="/atletas" 
          className="px-8 py-3 border border-white hover:border-red-600 hover:text-red-600 text-white rounded-full font-bold transition-all duration-300"
        >
          {t('home.cta.viewAthletes')}
        </a>
      </div>
    </div>
  </div>
</section>

{/* CSS adicional para animaciones */}
<style jsx>{`
  @keyframes growWidth {
    from { width: 0; }
    to { width: 100%; }
  }
  
  @keyframes growWidthDelayed {
    0% { width: 0; }
    30% { width: 0; }
    100% { width: 100%; }
  }
  
  @keyframes growWidthDelayed2 {
    0% { width: 0; }
    60% { width: 0; }
    100% { width: 100%; }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-growWidth {
    animation: growWidth 3s forwards;
  }
  
  .animate-growWidthDelayed {
    animation: growWidthDelayed 4s forwards;
  }
  
  .animate-growWidthDelayed2 {
    animation: growWidthDelayed2 5s forwards;
  }
  
  .animate-fadeInUp {
    animation: fadeInUp 1s forwards;
  }
  
  .hide-scrollbar::-webkit-scrollbar {
    display: none;
  }
  
  .hide-scrollbar {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`}</style>
</div>
);
};

export default Inicio;